import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ForumPost } from '../types/forum';
import { mockPosts as initialPosts } from '../data/mockPosts';
import { supabase } from '../lib/supabase';
import { notifyLikeOnBoardPost, notifyCommentOnBoardPost, isBoardPost } from '../utils/notifications';

interface ForumStore {
  likedPosts: string[];
  likedComments: string[];
  _isProcessingLike?: boolean;
  _processingComments?: Set<string>;
  likePost: (postId: string) => void;
  likeComment: (commentId: string) => void;
  hasLiked: (postId: string) => boolean;
  hasLikedComment: (commentId: string) => boolean;
  addComment: (postId: string, content: string) => Promise<void>;
}

export const useForumStore = create<ForumStore>()(
  persist(
    (set, get) => ({
      likedPosts: [],
      likedComments: [],
      _processingComments: new Set<string>(),
      
              likePost: async (postId: string) => {
          const hasLiked = get().likedPosts.includes(postId);
          
          try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Bruker ikke autentisert');

            // Sjekk faktisk database-status før operasjon
            const { data: actualLike, error: actualError } = await supabase
              .from('likes')
              .select('is_liked')
              .eq('post_id', parseInt(postId))
              .eq('user_id', user.id)
              .single();

            const actualHasLiked = !actualError && actualLike?.is_liked;

            // Finn alle eksisterende rader (rydd opp ev. duplikater ved å oppdatere alle)
            const { data: existingLikes, error: checkError } = await supabase
              .from('likes')
              .select('post_id, user_id, is_liked')
              .eq('post_id', parseInt(postId))
              .eq('user_id', user.id);

            if (checkError) throw checkError;



            if (existingLikes && existingLikes.length > 0) {
              // Toggle til motsatt verdi basert på faktisk database-status
              const newIsLiked = !actualHasLiked;
              
              const { error } = await supabase
                .from('likes')
                .update({ is_liked: newIsLiked })
                .eq('post_id', parseInt(postId))
                .eq('user_id', user.id);

              if (error) {
                console.error('Update error:', error);
                throw error;
              }
            } else {
              // Opprett ny rad
              const { error } = await supabase
                .from('likes')
                .insert({
                  post_id: parseInt(postId),
                  user_id: user.id,
                  is_liked: true
                });

              if (error) {
                console.error('Insert error:', error);
                throw error;
              }
            }

                      // Notifikasjon kun ved like
            if (!actualHasLiked) {
              const boardPostInfo = await isBoardPost(parseInt(postId));
              if (boardPostInfo.isBoard && boardPostInfo.residenceId && boardPostInfo.title) {
                const user = (await supabase.auth.getUser()).data.user;
                if (user) {
                  const { data: profile } = await supabase
                    .from('profiles')
                    .select('first_name, last_name')
                    .eq('id', user.id)
                    .single();

                  const userName = profile ? `${profile.first_name} ${profile.last_name}`.trim() : 'En beboer';
                  await notifyLikeOnBoardPost(parseInt(postId), boardPostInfo.residenceId, userName, boardPostInfo.title);
                }
              }
            }

          // Oppdater lokal state
          set((state) => {
            const newLikedPosts = hasLiked
              ? state.likedPosts.filter(id => id !== postId)
              : [...state.likedPosts, postId];

            return {
              likedPosts: newLikedPosts
            };
          });
        } catch (error) {
          console.error('Error toggling like:', error);
          throw error;
        }
      },

                likeComment: async (commentId: string) => {
          // Forhindre dobbel-klikk med Set-basert sjekk
          const currentState = get();
          
          // Sikre at _processingComments er initialisert
          if (!currentState._processingComments) {
            currentState._processingComments = new Set<string>();
          }
          
          if (currentState._processingComments.has(commentId)) {
            return;
          }

          const hasLiked = currentState.likedComments.includes(commentId);

                      try {
              // Sett flagg for å forhindre dobbel-klikk
              const newProcessingComments = new Set(currentState._processingComments || new Set<string>());
              newProcessingComments.add(commentId);
              set({ 
                _isProcessingLike: true,
                _processingComments: newProcessingComments
              });

            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Bruker ikke autentisert');

            // Sjekk om brukeren tilhører et boretslag (nødvendig for RLS)
            const { data: userBelongs, error: userBelongsError } = await supabase
              .from('user_belong')
              .select('residence_id')
              .eq('profiles_id', user.id);

            if (userBelongsError || !userBelongs || userBelongs.length === 0) {
              throw new Error('Bruker tilhører ikke noe boretslag');
            }



            // Sjekk om kommentaren eksisterer og brukeren har tilgang til den
            const { data: comment, error: commentError } = await supabase
              .from('comment')
              .select('id, post_id')
              .eq('id', parseInt(commentId))
              .single();

            if (commentError || !comment) {
              throw new Error('Kommentar ikke funnet');
            }



            // Sjekk om det allerede finnes en rad for denne brukeren og kommentaren
            const { data: existingLike, error: existingError } = await supabase
              .from('comment_like')
              .select('*')
              .eq('comment_id', parseInt(commentId))
              .eq('user_id', user.id)
              .single();

            if (existingError && existingError.code !== 'PGRST116') {
              // PGRST116 = "not found", som er OK
              console.error('Error checking existing like:', existingError);
              throw existingError;
            }

            if (existingLike) {
              // Oppdater eksisterende rad
              const { error } = await supabase
                .from('comment_like')
                .update({ is_liked: !hasLiked })
                .eq('comment_id', parseInt(commentId))
                .eq('user_id', user.id);

              if (error) {
                console.error('Error updating comment like:', error);
                throw error;
              }
  
            } else {
              // Opprett ny rad
              const insertData = {
                comment_id: parseInt(commentId),
                user_id: user.id,
                is_liked: true
              };
  

              const { data, error } = await supabase
                .from('comment_like')
                .insert(insertData)
                .select();

              if (error) {
                console.error('Error adding comment like:', error);
                throw error;
              }
  
            }

            // Update local state basert på faktisk database-resultat
            set((state) => {
              const newLikedComments = !hasLiked
                ? [...state.likedComments, commentId]
                : state.likedComments.filter(id => id !== commentId);

  

              const finalProcessingComments = new Set(currentState._processingComments || new Set<string>());
              finalProcessingComments.delete(commentId);
              
              return {
                likedComments: newLikedComments,
                _isProcessingLike: false,
                _processingComments: finalProcessingComments
              };
            });
          } catch (error) {
            console.error('Feil ved like/unlike av kommentar:', error);
            // Reset flagg ved feil
            const errorProcessingComments = new Set(currentState._processingComments || new Set<string>());
            errorProcessingComments.delete(commentId);
            set({ 
              _isProcessingLike: false,
              _processingComments: errorProcessingComments
            });
            throw error;
          }
        },
      
      hasLiked: (postId: string) => {
        return get().likedPosts.includes(postId);
      },


      
      hasLikedComment: (commentId: string) => {
        return get().likedComments.includes(commentId);
      },

      // Last brukerens comment likes fra databasen
      loadUserCommentLikes: async () => {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return;

          const { data: userCommentLikes, error } = await supabase
            .from('comment_like')
            .select('comment_id')
            .eq('user_id', user.id)
            .eq('is_liked', true);

          if (!error && userCommentLikes) {
            const likedCommentIds = userCommentLikes.map(like => like.comment_id.toString());
            set({ likedComments: likedCommentIds });

          }
        } catch (error) {
          console.error('Error loading user comment likes:', error);
        }
      },



      
      addComment: async (postId: string, content: string) => {
        try {
          console.log('🔍 addComment kalt for postId:', postId, 'content:', content);
          
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) throw new Error('Bruker ikke autentisert');

          // Legg til kommentar i databasen
          const { data: comment, error } = await supabase
            .from('comment')
            .insert({
              post_id: parseInt(postId),
              user_id: user.id,
              content: content.trim()
            })
            .select()
            .single();

          if (error) throw error;
          console.log('✅ Kommentar lagt til i database:', comment);

          // Sjekk om innlegget tilhører styret og opprett notifikasjon
          console.log('🔍 Sjekker om post tilhører styret...');
          const boardInfo = await isBoardPost(parseInt(postId));
          console.log('📋 Board info:', boardInfo);
          
          if (boardInfo.isBoard && boardInfo.residenceId && boardInfo.title) {
            console.log('✅ Post tilhører styret, oppretter notifikasjon...');
            
            const { data: userProfile } = await supabase
              .from('profiles')
              .select('first_name, last_name')
              .eq('id', user.id)
              .single();

            const userName = userProfile 
              ? `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim()
              : 'En beboer';

            console.log('👤 Brukernavn:', userName);

            await notifyCommentOnBoardPost(
              parseInt(postId),
              boardInfo.residenceId,
              userName,
              boardInfo.title,
              content.trim()
            );
            console.log('✅ Notifikasjon opprettet!');
          } else {
            console.log('❌ Post tilhører ikke styret eller mangler info');
          }
        } catch (error) {
          console.error('❌ Feil ved lagring av kommentar:', error);
          throw error;
        }
      }
    }),
    {
      name: 'forum-store',
      version: 1,
      // Ekskluder _processingComments fra persist siden Set ikke serialiseres riktig
      partialize: (state) => ({
        likedPosts: state.likedPosts,
        likedComments: state.likedComments,
      }),
    }
  )
);